// Captura + gráfico de signos vitales del registro transanestésico (018A) y
// de monitorización/reevaluación (019) — Fase 1B, ver PLAN.md. Reusa
// `GraficoSerieTemporal` (motor genérico ya usado por 020/curvas de
// crecimiento) en vez de un SVG bespoke: es estructuralmente una serie
// temporal más (eje X en minutos con eventos discretos superpuestos).
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { LIMITES_ESPECIFICACION } from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraficoSerieTemporal } from '../../../graficos/GraficoSerieTemporal';
import { eventosGraficoDe, seriesSignosVitalesAnestesiaDe } from './datos';
import { CATALOGO_EVENTOS, CATALOGO_PARAMETROS, MINUTO_MAX, minutoValido, type EventoAnestesia, type MedicionPuntual } from './tipos';

export interface GraficoSignosVitalesAnestesiaProps {
  readonly mediciones: readonly MedicionPuntual[];
  readonly onChangeMediciones: (v: readonly MedicionPuntual[]) => void;
  readonly eventos: readonly EventoAnestesia[];
  readonly onChangeEventos: (v: readonly EventoAnestesia[]) => void;
  /** Último minuto que admite el papel del formulario (018A: 540, 019: 360). */
  readonly minutoMax?: number;
}

function formatoMinutos(x: number): string {
  return `${x}min`;
}

export function GraficoSignosVitalesAnestesia({ mediciones, onChangeMediciones, eventos, onChangeEventos, minutoMax = MINUTO_MAX }: GraficoSignosVitalesAnestesiaProps): JSX.Element {
  const { t } = useTranslation();
  const [parametroNuevo, setParametroNuevo] = useState(CATALOGO_PARAMETROS[0]!.clave);
  const [minutoNuevo, setMinutoNuevo] = useState<number | ''>('');
  const [valorNuevo, setValorNuevo] = useState<number | ''>('');
  const [eventoNuevo, setEventoNuevo] = useState(CATALOGO_EVENTOS[0]!.clave);
  const [minutoEventoNuevo, setMinutoEventoNuevo] = useState<number | ''>('');

  const series = useMemo(() => seriesSignosVitalesAnestesiaDe(mediciones), [mediciones]);
  const eventosGrafico = useMemo(() => eventosGraficoDe(eventos), [eventos]);

  const medicionesLlenas = mediciones.length >= LIMITES_ESPECIFICACION.puntosSignosVitales;
  const eventosLlenos = eventos.length >= LIMITES_ESPECIFICACION.eventosSignosVitales;

  function agregarMedicion(): void {
    if (medicionesLlenas || minutoNuevo === '' || valorNuevo === '' || !minutoValido(minutoNuevo, minutoMax)) return;
    onChangeMediciones([...mediciones, { id: crypto.randomUUID(), parametro: parametroNuevo, minuto: minutoNuevo, valor: valorNuevo }]);
    setMinutoNuevo('');
    setValorNuevo('');
  }

  function agregarEvento(): void {
    if (eventosLlenos || minutoEventoNuevo === '' || !minutoValido(minutoEventoNuevo, minutoMax)) return;
    onChangeEventos([...eventos, { id: crypto.randomUUID(), tipo: eventoNuevo, minuto: minutoEventoNuevo }]);
    setMinutoEventoNuevo('');
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.signosVitalesAnestesia.parametro')}</Label>
          <Select value={parametroNuevo} onValueChange={(v) => setParametroNuevo(v as typeof parametroNuevo)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATALOGO_PARAMETROS.map((def) => (
                <SelectItem key={def.clave} value={def.clave}>
                  {def.etiqueta} ({def.unidad})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.signosVitalesAnestesia.minuto')}</Label>
          <Input type="number" min={0} max={minutoMax} className="w-24" value={minutoNuevo} onChange={(e) => setMinutoNuevo(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.signosVitalesAnestesia.valor')}</Label>
          <Input type="number" className="w-24" value={valorNuevo} onChange={(e) => setValorNuevo(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <Button type="button" variant="outline" className="self-end" disabled={medicionesLlenas} onClick={agregarMedicion}>
          {t('formularios.signosVitalesAnestesia.agregarMedicion')}
        </Button>
      </div>

      {(medicionesLlenas || eventosLlenos) && <p className="text-xs text-hce-clay">{t('formularios.signosVitalesAnestesia.topeAlcanzado')}</p>}

      {mediciones.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {mediciones.map((m) => {
            const def = CATALOGO_PARAMETROS.find((d) => d.clave === m.parametro);
            return (
              <li key={m.id} className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-hce-ink">
                {def?.etiqueta ?? m.parametro} {m.valor}
                {def?.unidad} @{m.minuto}min
                <button type="button" aria-label={t('formularios.signosVitalesAnestesia.quitarMedicion')} onClick={() => onChangeMediciones(mediciones.filter((x) => x.id !== m.id))}>
                  <Trash2 className="size-3.5 text-hce-muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.signosVitalesAnestesia.evento')}</Label>
          <Select value={eventoNuevo} onValueChange={(v) => setEventoNuevo(v as typeof eventoNuevo)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATALOGO_EVENTOS.map((def) => (
                <SelectItem key={def.clave} value={def.clave}>
                  {def.etiqueta}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.signosVitalesAnestesia.minuto')}</Label>
          <Input type="number" min={0} max={minutoMax} className="w-24" value={minutoEventoNuevo} onChange={(e) => setMinutoEventoNuevo(e.target.value === '' ? '' : Number(e.target.value))} />
        </div>
        <Button type="button" variant="outline" className="self-end" disabled={eventosLlenos} onClick={agregarEvento}>
          {t('formularios.signosVitalesAnestesia.agregarEvento')}
        </Button>
      </div>

      {eventos.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {eventos.map((e) => {
            const def = CATALOGO_EVENTOS.find((d) => d.clave === e.tipo);
            return (
              <li key={e.id} className="flex items-center gap-1.5 rounded-full border border-hce-clay/40 px-2.5 py-1 text-xs text-hce-ink">
                {def?.etiqueta ?? e.tipo} @{e.minuto}min
                <button type="button" aria-label={t('formularios.signosVitalesAnestesia.quitarEvento')} onClick={() => onChangeEventos(eventos.filter((x) => x.id !== e.id))}>
                  <Trash2 className="size-3.5 text-hce-muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <GraficoSerieTemporal
        series={series}
        eventos={eventosGrafico}
        formatoEjeX={formatoMinutos}
        etiquetaEjeY={t('formularios.signosVitalesAnestesia.ejeY')}
        aria={{ label: t('formularios.signosVitalesAnestesia.tituloGrafico', { max: minutoMax }) }}
      />
    </div>
  );
}
